import { z } from 'zod';
import { ClientProjectActivityTypeSchema } from './client.js';
import { MAX_MINOR_UNITS, CalendarDateSchema, ClientProjectStatusSchema, UnitTypeSchema, saVatNumberSchema } from './common.js';
import { LedgerAccountSectionSchema, LedgerAccountSystemRoleSchema } from '../ledger.js';
import { VatTreatmentSchema } from '../vat.js';
import { OpeningRetagInputSchema } from './invoice.js';
import { nullableText } from '../sanitised-text.js';
// =============================================================================
// Bulk Import
// =============================================================================
export const BULK_IMPORT_MAX_ROWS = 1000;
export const RecognitionOutcomeSchema = z.object({
    status: z.enum(['posted', 'skipped', 'failed']),
    reason: z.string().optional(),
});
export const BulkImportRowResultSchema = z.discriminatedUnion('status', [
    z.object({
        row: z.number(),
        status: z.literal('created'),
        id: z.string(),
        recognition: RecognitionOutcomeSchema.optional(),
        /** An opening row's itemisation outcome; opening rows carry no `recognition`. */
        openingItem: RecognitionOutcomeSchema.optional(),
        /**
         * Steering warning for an off-increment hours row created unconverted by
         * the activity bulk import (see `client-project-activity.ts` `warnings`).
         * Absent when the row raised nothing to flag, or does not apply (not an
         * hours row, or the account has no configured increment).
         */
        warnings: z.array(z.string()).optional(),
    }),
    z.object({
        row: z.number(),
        status: z.literal('skipped'),
        reason: z.string(),
        recognition: RecognitionOutcomeSchema.optional(),
        openingItem: RecognitionOutcomeSchema.optional(),
    }),
    z.object({
        row: z.number(),
        status: z.literal('error'),
        message: z.string(),
    }),
]);
export const BulkImportSummarySchema = z.object({
    total: z.number(),
    created: z.number(),
    skipped: z.number(),
    errors: z.number(),
});
export const BulkImportResponseSchema = z.object({
    results: z.array(BulkImportRowResultSchema),
    summary: BulkImportSummarySchema,
});
// -- Bulk client import --
export const BulkCreateClientItemSchema = z.object({
    name: z.string().min(1, 'Client name is required').max(200),
    email: z.email().max(200).optional().nullable(),
    email2: z.email().max(200).optional().nullable(),
    phone: nullableText(),
    defaultRateMinorUnits: z.int().min(1).optional().nullable(),
    defaultRateCurrency: z.string().length(3).optional(),
    // Business entity fields (optional - upserted after client creation)
    legalName: nullableText(300),
    vatNumber: saVatNumberSchema,
    addressLine1: nullableText(300),
    addressLine2: nullableText(300),
    addressCity: nullableText(100),
    addressProvince: nullableText(100),
    addressPostalCode: nullableText(20),
    addressCountry: nullableText(100),
});
export const BulkCreateClientsInputSchema = z.object({
    rows: z.array(BulkCreateClientItemSchema).min(1).max(BULK_IMPORT_MAX_ROWS),
});
// -- Bulk client project import --
export const BulkCreateClientProjectItemSchema = z.object({
    clientName: z.string().min(1, 'Client name is required'),
    name: z.string().min(1, 'Matter name is required').max(200),
    // Maps to the matter's `customReference` (the user-defined display label,
    // surfaced as the "Custom Reference" column in the import template). It is
    // optional: when omitted, the service auto-generates the internal reference
    // and that is shown instead. A supplied value appears as the user-visible
    // label on lists, invoices, and emails.
    reference: nullableText(100),
    description: nullableText(2000),
    defaultRateMinorUnits: z.int().min(1).optional().nullable(),
    currency: z.string().length(3).optional(),
    status: ClientProjectStatusSchema.optional(),
});
export const BulkCreateClientProjectsInputSchema = z.object({
    rows: z.array(BulkCreateClientProjectItemSchema).min(1).max(BULK_IMPORT_MAX_ROWS),
});
// -- Bulk activity import --
export const BulkCreateActivityItemSchema = z.object({
    clientName: z.string().min(1, 'Client name is required'),
    reference: z.string().min(1, 'Matter reference is required'),
    userEmail: z.email().optional().nullable(),
    type: ClientProjectActivityTypeSchema.optional(),
    description: z.string().min(1, 'Description is required').max(2000),
    date: CalendarDateSchema,
    units: z.number().gt(0, 'Units must be greater than 0').optional().nullable(),
    ratePerUnitMinorUnits: z.int().min(1).optional().nullable(),
    currency: z.string().length(3).optional().nullable(),
    // Optional on bulk import so existing CSV formats remain valid. The
    // bulk-import service falls back to the registry defaults for the given
    // `type` when unitType / isDisbursement are omitted.
    unitType: UnitTypeSchema.optional(),
    isDisbursement: z.boolean().optional(),
});
export const BulkCreateActivitiesInputSchema = z.object({
    rows: z.array(BulkCreateActivityItemSchema).min(1).max(BULK_IMPORT_MAX_ROWS),
});
// -- Bulk invoice import --
export const BulkCreateInvoiceLineItemSchema = z.object({
    description: z.string().min(1, 'Line description is required').max(2000),
    units: z.number().optional().nullable(),
    ratePerUnitMinorUnits: z.int().min(0).optional().nullable(),
    totalAmountMinorUnits: z.int().min(0, 'Line amount must be non-negative'),
    /** Exact source VAT in cents; omitted/null retains rate-based calculation. */
    vatAmountMinorUnits: z.number().int().min(0).max(MAX_MINOR_UNITS).nullable().optional(),
    vatRateBasisPoints: z.int().min(0).max(10000).optional().nullable(),
    clientName: z.string().min(1, 'Client name is required'),
    reference: nullableText(),
    date: CalendarDateSchema.optional().nullable(),
    // Optional per-line GL account. When set, the bulk-import service persists the
    // line with this ledger account and recognition posts the line's income to it
    // (stampNullLineLedgerDefaults skips a line that already carries an account).
    // Null/absent leaves the line null so the null-line stamp applies the default
    // rule: Recovery Income (DEFAULT_RECOVERY) for a disbursement when the ledger
    // has it, else DEFAULT_FEE; pass an explicit account to recognise a recovery
    // on Disbursements Recoverable. The CLI resolves
    // a "Line Account Code" CSV column to this id against the account's chart; the
    // service validates it belongs to the account's active business ledger.
    ledgerAccountId: z.uuid().optional().nullable(),
});
/** Imported statuses that leave a document open (owing), and so the only ones that can carry an opening amount. */
export const OPEN_IMPORTED_INVOICE_STATUSES = ['APPROVED', 'SENT'];
export function isOpenImportedInvoiceStatus(status) {
    return OPEN_IMPORTED_INVOICE_STATUSES.includes(status);
}
export const BulkCreateInvoiceItemSchema = z.object({
    invoiceNumber: z.string().min(1, 'Invoice number is required').max(50),
    clientName: z.string().min(1, 'Client name is required'),
    date: CalendarDateSchema,
    dueDate: CalendarDateSchema.optional().nullable(),
    status: z.enum(['DRAFT', 'APPROVED', 'SENT', 'PAID', 'VOIDED']).default('APPROVED'),
    currency: z.string().length(3).default('ZAR'),
    vatRateBasisPoints: z.int().min(0).max(10000).default(0),
    notes: nullableText(5000),
    reference: nullableText(100),
    // Optional CSV "Bank Account" column carrying the bank account NUMBER (not
    // an id). The bulk-import service normalises it and matches it against the
    // account's own bank accounts. A match links invoice.bankAccountId; blank or
    // absent values fall back to the account default; non-empty unmatched values
    // leave invoice.bankAccountId null.
    bankAccountNumber: z.string().max(50).optional().nullable(),
    lineItems: z.array(BulkCreateInvoiceLineItemSchema).min(1, 'At least one line item is required').max(500),
    /**
     * Marks the row as an opening debtor: what the client still owed at the
     * ledger's conversion date. The invoice keeps its CSV number and amounts and
     * is approved as an opening item (no income or VAT posted) instead of being
     * recognised. Only an open (APPROVED or SENT) row can carry one, and a
     * request carrying one on any row needs a super-admin.
     */
    openAmountMinorUnits: OpeningRetagInputSchema.shape.openAmountMinorUnits.optional(),
}).refine(row => row.openAmountMinorUnits === undefined || isOpenImportedInvoiceStatus(row.status), { message: 'An opening outstanding amount applies only to APPROVED or SENT rows', path: ['openAmountMinorUnits'] });
/** The row carries an Open Amount, so it is an opening debtor rather than an ordinary import. */
export function isOpeningInvoiceRow(row) {
    return row.openAmountMinorUnits !== undefined;
}
export const BulkCreateInvoicesInputSchema = z.object({
    rows: z.array(BulkCreateInvoiceItemSchema).min(1).max(BULK_IMPORT_MAX_ROWS),
    updateSequence: z.boolean().default(true),
});
// -- Bulk ledger-account import --
export const BulkCreateLedgerAccountItemSchema = z.object({
    // Account codes are system-assigned from the per-type sequence; the import
    // does not accept a code.
    name: z.string().min(1).max(255),
    section: LedgerAccountSectionSchema,
    // Optional system role (control account, VAT, equity). Null/omitted for
    // ordinary accounts. Each ledger allows at most one account per role.
    systemRole: LedgerAccountSystemRoleSchema.nullable().optional(),
    // Optional opening balance in major units (e.g. 1500.00). Positive = debit,
    // negative = credit. Balances across all rows must net to zero. Bounded to
    // keep `balance * 100` within the entry-set minor-unit range and well clear
    // of integer overflow.
    balance: z.number().finite().min(-99_999_999_999.99).max(99_999_999_999.99).optional(),
    // Optional per-account VAT treatment for direct bank-line allocation splits.
    // Null/omitted = legacy/unset (gross-post).
    defaultVatTreatment: VatTreatmentSchema.nullable().optional(),
});
export const BulkCreateLedgerAccountsInputSchema = z.object({
    accounts: z.array(BulkCreateLedgerAccountItemSchema).min(1).max(BULK_IMPORT_MAX_ROWS),
    idempotencyKey: z.string().min(1).max(255),
});
